import React, {useCallback, useEffect, useState} from 'react';
import {formatDateTime} from '../utils/dateTime';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {hasPermission, useWardrobe} from "../context/WardrobeContext";
import Spinner from "../components/Spinner";
import AuthImage from "../components/AuthImage";
import {ShirtIcon} from "../components/icons";
import {pluralDays, pluralItems} from '../utils/plural';
import {
    DisposalItemSafety,
    DisposalSafetyLevel,
    DisposalTrigger,
    DisposalTriggerSummary,
    simulateDisposalSafety,
} from '../service/DisposalApi';
import DecideIntentDialog from '../components/disposal/DecideIntentDialog';
import {DisposalIntent} from '../support/itemLifecycle';
import logger from '../utils/logger';

interface Coverage {
    categoryName: string | null;
    remainingInCategory: number;
    safeToDispose: boolean;
    /** Аудит 17.09, D02: замена считается по функциональной роли, а не по категории. */
    roleNameRu: string | null;
    remainingInRole: number;
    safetyLevel: DisposalSafetyLevel | null;
    safetySummary: string | null;
}

interface Candidate {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    totalWears: number;
    rating: number | null;
    itemScore: number | null;
    /** Тот же балл в пользовательской шкале 0–100 — именно он идёт в подпись. */
    itemScore100: number | null;
    lastWornAt: string | null;
    createdAt: string | null;
    disposalScore: number;
    reasons: string[];
    coverage: Coverage;
    /** Что именно сработало у этой вещи (аудит 17.09, D01). */
    triggers?: DisposalTrigger[] | null;
}

interface Queue {
    thresholdDays: number;
    totalCandidates: number;
    candidates: Candidate[];
    // analytics-deeplinks-bg-disposal (FR-6): источник расчёта и его время (additive)
    source?: 'snapshot' | 'sync' | null;
    computedAt?: string | null;
    /** Состав очереди по триггерам — подпись страницы строится из него (D01). */
    triggerSummary?: DisposalTriggerSummary[] | null;
    /** Журнала носки хватает, чтобы судить об использовании (D01). */
    wearHistorySufficient?: boolean;
}

/**
 * Подпись страницы по фактическому составу очереди (аудит 17.09, D01).
 *
 * <p>Прежний текст «Залежалось по порогу стратегии (150 дней)» стоял над очередью всегда —
 * даже когда единственным кандидатом были порванные шорты, а журнала носки не хватало даже на
 * подсчёт залежавшихся. Заголовок обязан следовать за содержимым, а не наоборот.</p>
 */
export function queueSubtitle(queue: Queue): string {
    const summary = queue.triggerSummary ?? [];
    if (summary.length === 0) {
        return 'Система нашла повод присмотреться к этим вещам. Решение — за вами.';
    }
    const parts = summary.map(t => `${t.label.toLowerCase()} — ${pluralItems(t.count)}`);
    return `Почему эти вещи здесь: ${parts.join('; ')}.`;
}

/** Цвет строки безопасности по уровню: уверенность зелёная, осторожность — янтарная. */
const SAFETY_TONE: Record<DisposalSafetyLevel, string> = {
    SAFE: 'text-emerald-600 dark:text-emerald-400',
    SEASON_GAP: 'text-amber-600 dark:text-amber-400',
    LAST_IN_ROLE: 'text-amber-600 dark:text-amber-400',
    UNKNOWN_ROLE: 'text-gray-500 dark:text-gray-400',
};

/**
 * Что останется, если вещь уйдёт (аудит 17.09, D02/R09). Пока вещь не выбрана — расчёт по ней
 * одной; как только она в группе, показывается результат для всей группы: это разные ответы.
 */
function SafetyLine({coverage, group}: {coverage: Coverage; group?: DisposalItemSafety}) {
    const level = group?.level ?? coverage.safetyLevel;
    const summary = group?.summary ?? coverage.safetySummary;
    if (!level || !summary) {
        // Старый контракт без роли: показываем то, что действительно проверено.
        return (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="disposal-safety">
                Останется в категории: {coverage.remainingInCategory}
            </div>
        );
    }
    return (
        <div className={`mt-1 text-xs ${SAFETY_TONE[level]}`} data-testid="disposal-safety">
            {summary}
            {group && <span className="text-gray-400 dark:text-gray-500"> · с учётом всего выбора</span>}
        </div>
    );
}

const DisposalQueuePage: React.FC = () => {
    const {selectedOwnerId, permissions} = useWardrobe();
    const canProcess = hasPermission(permissions, 'MOVE_TO_TRASH');

    const [data, setData] = useState<Queue | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [processing, setProcessing] = useState(false);
    // D02: безопасность зависит от состава выбора, поэтому пересчитывается при его изменении.
    const [safety, setSafety] = useState<Record<string, DisposalItemSafety>>({});
    // D06: способ выбирается до отправки — кнопка обязана называть решение, которое будет записано.
    const [deciding, setDeciding] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        setSelected(new Set());
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<Queue>('/disposal/queue', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useEffect(() => {
        load();
    }, [load]);

    /**
     * Пересчёт безопасности выбранной группы (аудит 17.09, D02). Три последние футболки по
     * отдельности каждая «не последняя», а вместе опустошают роль — поэтому ответ зависит от
     * всего выбора и обновляется на каждое изменение.
     */
    useEffect(() => {
        if (selected.size === 0) {
            setSafety({});
            return undefined;
        }
        let cancelled = false;
        void simulateDisposalSafety(selectedOwnerId, Array.from(selected))
            .then(result => {
                if (cancelled) return;
                const byId: Record<string, DisposalItemSafety> = {};
                result.items.forEach(i => {
                    byId[i.itemId] = i;
                });
                setSafety(byId);
            })
            .catch(e => {
                // Безопасность — подсказка, а не содержимое: её сбой не ломает страницу,
                // но и старый ответ показывать нельзя.
                logger.debug('DisposalQueuePage: safety failed', {error: (e as Error)?.message});
                if (!cancelled) setSafety({});
            });
        return () => {
            cancelled = true;
        };
    }, [selected, selectedOwnerId]);

    const toggle = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    /**
     * «Выбрать не единственные в роли» (аудит 17.09, D02). Прежняя кнопка называлась
     * «Выбрать безопасные» и обещала, что «останется чем заменить», проверяя при этом лишь
     * непустоту категории. Теперь и название, и условие говорят об одном и том же.
     */
    const selectAllSafe = () => {
        if (!data) return;
        setSelected(new Set(data.candidates
            .filter(c => c.coverage.safetyLevel === 'SAFE' || (!c.coverage.safetyLevel
                && c.coverage.safeToDispose))
            .map(c => c.id)));
    };

    const decide = async (intent: DisposalIntent) => {
        if (selected.size === 0) return;
        setDeciding(false);
        setProcessing(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            await api.post('/disposal/queue/process',
                {itemIds: Array.from(selected), intent}, {params});
            await load();
        } catch (e) {
            setError(true);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <Spinner/>;
    }

    if (error && !data) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить очередь на выбытие</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    const queue = data!;

    return (
        <div className="space-y-4">
            {deciding && (
                <DecideIntentDialog
                    count={selected.size}
                    busy={processing}
                    onConfirm={intent => void decide(intent)}
                    onCancel={() => setDeciding(false)}
                />
            )}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Кандидаты</h2>
                {/* Q5: очередь — не «всё, что стоит выбыть», а один срез по порогу стратегии.
                    Прежний подзаголовок обещал больше, чем экран показывает. */}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-testid="disposal-subtitle">
                    {queueSubtitle(queue)} Решение обратимо: вещь остаётся у вас,
                    пока вы её не передадите.
                </p>
                {queue.wearHistorySufficient === false && (
                    /* D01: пустой журнал — это «неизвестно», а не «не носили». */
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                       data-testid="disposal-history-unknown">
                        Истории носки пока мало: носили эти вещи или нет — неизвестно, и по сроку
                        без носки очередь ничего не утверждает.
                    </p>
                )}
                {queue.source === 'snapshot' && queue.computedAt && (
                    /* FR-6/FR-7: очередь сформирована фоном — показываем время расчёта */
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" data-testid="disposal-source">
                        Рассчитано фоном {formatDateTime(queue.computedAt)}
                    </p>
                )}
            </div>

            {error && (
                <p className="text-rose-600 dark:text-rose-400" data-testid="disposal-error">
                    Не удалось выполнить действие. Попробуйте ещё раз.
                </p>
            )}

            {queue.totalCandidates === 0 ? (
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-6 space-y-3"
                     data-testid="disposal-empty">
                    <p className="text-gray-700 dark:text-gray-200">
                        По порогу стратегии ({pluralDays(queue.thresholdDays)}) залежавшихся вещей нет.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Это не значит, что разбирать нечего: сюда попадают только вещи, которые
                        когда-то носили и перестали. Вещи без единой носки, дубли и всё остальное
                        разбирает стадия «Решения» — там вы решаете по каждой вещи.
                    </p>
                    <Link to="/disposal/decisions" data-testid="disposal-empty-declutter"
                          className="inline-flex items-center min-h-[44px] px-4 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                        Перейти к решениям
                    </Link>
                </div>
            ) : (
                <>
                    {canProcess && (
                        <div className="flex flex-wrap items-center gap-3" data-testid="disposal-actions">
                            <button
                                type="button"
                                onClick={selectAllSafe}
                                data-testid="disposal-select-safe"
                                title="Вещи, чью функциональную роль после их ухода закрывают другие вещи"
                                className="min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-700 dark:text-gray-200 hover:border-brand-500"
                            >
                                Выбрать не единственные в роли
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400 basis-full sm:basis-auto">
                                Считаем по функциональной роли (а не по категории) и с учётом того,
                                что уйдут все выбранные вещи сразу. Замену не обещаем — показываем,
                                что останется.
                            </span>
                            {/* D06: единственная primary-кнопка области называет решение, а не место,
                                куда вещь «кладут». Способ выбирается в диалоге — по нему видно, в какой
                                группе стадии «Ждут передачи» вещь окажется. */}
                            <button
                                type="button"
                                onClick={() => setDeciding(true)}
                                disabled={selected.size === 0 || processing}
                                data-testid="disposal-decide"
                                className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            >
                                {processing ? 'Сохраняем…' : `Решить расстаться… (${selected.size})`}
                            </button>
                            <Link to="/disposal/decisions" data-testid="disposal-review-link"
                                  className="inline-flex items-center min-h-[44px] px-4 rounded-xl text-sm
                                             text-brand-600 dark:text-brand-400 hover:underline">
                                Рассмотреть в разборе
                            </Link>
                        </div>
                    )}

                    <ul className="space-y-3" data-testid="disposal-list">
                        {queue.candidates.map(c => (
                            <li key={c.id}
                                className="rounded-xl border border-gray-200 dark:border-ink-700 p-3 flex gap-3 items-start">
                                {canProcess && (
                                    <input
                                        type="checkbox"
                                        checked={selected.has(c.id)}
                                        onChange={() => toggle(c.id)}
                                        aria-label={`Выбрать «${c.name}» для выбытия`}
                                        className="mt-1 accent-brand-600 w-5 h-5 shrink-0"
                                    />
                                )}
                                {/* FR-7: фото кандидата (миниатюра) со ссылкой на карточку вещи */}
                                <Link to={`/clothing/${c.id}`} className="shrink-0" tabIndex={-1} aria-hidden="true">
                                    {c.primaryPhotoUrl ? (
                                        <AuthImage
                                            imageKey={c.primaryPhotoUrl}
                                            alt={c.name}
                                            variant="thumbnail"
                                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gray-100 dark:bg-ink-700 flex items-center justify-center text-gray-300 dark:text-gray-600">
                                            <ShirtIcon className="w-8 h-8"/>
                                        </div>
                                    )}
                                </Link>
                                <div className="min-w-0 flex-1">
                                    <div className="flex justify-between gap-2">
                                        <span className="font-medium truncate">
                                            {/* FR-7: имя — ссылка на вещь, тач-зона ≥ 44px за счёт паддинга строки */}
                                            <Link to={`/clothing/${c.id}`}
                                                className="text-gray-900 dark:text-gray-100 hover:text-brand-600 dark:hover:text-brand-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
                                                {c.name}
                                            </Link>
                                            {c.brandName && <span className="text-gray-500"> · {c.brandName}</span>}
                                        </span>
                                        {c.categoryName && (
                                            <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                                                {c.categoryName}
                                            </span>
                                        )}
                                    </div>
                                    {c.itemScore100 != null && (
                                        /* QA-15: «ItemScore: 1432» — внутренняя шкала; показываем 0–100. */
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                                           title="Ценность вещи по шкале от 0 до 100: 50 — нейтральная середина">
                                            Ценность вещи: {c.itemScore100}/100
                                        </p>
                                    )}
                                    {(c.triggers?.length ?? 0) > 0 && (
                                        /* D01: почему вещь здесь — фактами, а не общим текстом. */
                                        <div className="mt-1 flex flex-wrap gap-1"
                                             data-testid="disposal-triggers">
                                            {c.triggers!.map(t => (
                                                <span key={t.code} title={t.detail ?? undefined}
                                                      className="px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10
                                                                 text-xs text-amber-800 dark:text-amber-300">
                                                    {t.label}{t.detail ? `: ${t.detail}` : ''}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {c.reasons.map((r, idx) => (
                                            <span key={idx}
                                                  className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-xs text-gray-700 dark:text-gray-200">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                    <SafetyLine coverage={c.coverage} group={safety[c.id]}/>
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export default DisposalQueuePage;
